import {
	PAGESIZE_DEFAULT,
	SEARCH_KEYWORDS,
} from "@/lib/_search-providers/constants";
import { SearchFacet, SortByOption } from "@/lib/_search-providers/types";
import { usePathname, useSearchParams } from "next/navigation";
import { Dispatch, useEffect, useMemo, useReducer, useState } from "react";
import { SearchContextProviderProps } from "./SearchContextProvider";

export const SearchContextActionType = {
	RESET_STATE: "RESET_STATE",
	UPDATE_SORT_BY: "UPDATE_SORT_BY",
	UPDATE_PAGE_SIZE: "UPDATE_PAGE_SIZE",
	UPDATE_FACET: "UPDATE_FACET",
	UPDATE_FACETS: "UPDATE_FACETS",
} as const;

export type SearchContextActionType =
	(typeof SearchContextActionType)[keyof typeof SearchContextActionType];

type SearchContextAction =
	| {
			type: typeof SearchContextActionType.UPDATE_SORT_BY;
			payload: SortByOption | undefined;
	  }
	| {
			type: typeof SearchContextActionType.UPDATE_PAGE_SIZE;
			payload: number;
	  }
	| {
			type: typeof SearchContextActionType.UPDATE_FACET;
			payload: { facetName: string; facetValue: string; active: boolean };
	  }
	| {
			type: typeof SearchContextActionType.UPDATE_FACETS;
			payload: SearchFacet[];
	  }
	| {
			type: typeof SearchContextActionType.RESET_STATE;
	  };

export interface SearchContextState {
	links: {
		apply: string;
		clearAll: string;
	};
	sortBy: {
		active: SortByOption | undefined;
		selected: SortByOption | undefined;
		default: SortByOption | undefined;
	};
	pageSize: {
		active: number;
		selected: number;
		default: number;
	};
	facets: SearchFacet[];
	defaults: {
		sortBy: SortByOption | undefined;
		pageSize: number;
	};
}

type SearchContextReducerProps = Exclude<
	SearchContextProviderProps,
	"children" | "searchRequest" | "searchRequest"
>;

interface StateFromProps extends SearchContextReducerProps {
	pathname: string;
	currentParams?: URLSearchParams;
}

const createStateFromProps = (props: StateFromProps): SearchContextState => {
	const pathname = props.pathname || "";
	const defaultSortBy = Object.values(props.meta.sortBy).find(
		(option) => option.isDefault,
	);

	// get active sortBy from meta, fallback to default
	let activeSortBy = Object.values(props.meta.sortBy).find(
		(option) => option.isActive,
	);

	if (!activeSortBy) {
		activeSortBy = Object.values(props.meta.sortBy).find(
			(option) => option.isDefault,
		);
	}

	const state = {
		links: {
			apply: pathname,
			clearAll: pathname,
		},
		facets: props.meta.facets, // Assuming props.facets is fresh from API/server, so reference is fine initially
		defaults: {
			sortBy: defaultSortBy,
			pageSize: PAGESIZE_DEFAULT,
		},
		sortBy: {
			active: activeSortBy,
			selected: activeSortBy,
			default: defaultSortBy,
		},
		pageSize: {
			active: props.searchRequest.perPage || PAGESIZE_DEFAULT,
			selected: props.searchRequest.perPage || PAGESIZE_DEFAULT,
			default: PAGESIZE_DEFAULT,
		},
	};

	return {
		...state,
		links: updateLinks(state, pathname, props.currentParams),
	};
};

export const updateLinks = (
	state: SearchContextState,
	pathname: string,
	currentParams?: URLSearchParams,
): SearchContextState["links"] => {
	const { sortBy, pageSize, defaults, facets } = state;

	// Start from the current URL params so unrelated ones (`query`, tracking,
	// ...) survive. State-derived params are rewritten below.
	const params = new URLSearchParams(currentParams);

	// Facets and page are not held in state, so stale values must be dropped
	// rather than re-serialized.
	for (const key of [...params.keys()]) {
		if (
			key.startsWith(SEARCH_KEYWORDS.filter) ||
			key === SEARCH_KEYWORDS.page
		) {
			params.delete(key);
		}
	}

	// If the sortBy is not the default, set the sortBy parameter
	if (sortBy.selected && sortBy.selected.value !== defaults.sortBy?.value) {
		params.set(SEARCH_KEYWORDS.sortBy, sortBy.selected.value);
	} else {
		params.delete(SEARCH_KEYWORDS.sortBy);
	}

	// If the pageSize is not the default, set the pageSize parameter
	if (pageSize.selected !== defaults.pageSize) {
		params.set(SEARCH_KEYWORDS.pageSize, String(pageSize.selected));
	} else {
		params.delete(SEARCH_KEYWORDS.pageSize);
	}

	facets.forEach((facet) => {
		const selectedOptions = facet.options.filter((option) => option.isSelected);

		if (selectedOptions.length > 0) {
			params.set(
				`${SEARCH_KEYWORDS.filter}${facet.value}`,
				selectedOptions.map((option) => option.value).join(","),
			);
		}
	});

	const paramsString = params.toString();
	const href = `${pathname}${paramsString ? `?${paramsString}` : ""}`;

	// "Clear filters" only drops state-derived params, keeping e.g. `query`
	const clearParams = new URLSearchParams(currentParams);
	for (const key of [...clearParams.keys()]) {
		if (
			key.startsWith(SEARCH_KEYWORDS.filter) ||
			key === SEARCH_KEYWORDS.sortBy ||
			key === SEARCH_KEYWORDS.pageSize ||
			key === SEARCH_KEYWORDS.page
		) {
			clearParams.delete(key);
		}
	}
	const clearString = clearParams.toString();

	return {
		apply: href,
		clearAll: `${pathname}${clearString ? `?${clearString}` : ""}`,
	};
};

// Reducer function (exported for testing)
export const searchContextReducer = (
	state: SearchContextState,
	action: SearchContextAction,
	initialState?: SearchContextState, // Optional for testing/reset
	pathname = "", // Passed if needed for updateLinks
	currentParams?: URLSearchParams,
): SearchContextState => {
	let updatedState = state;

	switch (action.type) {
		case SearchContextActionType.RESET_STATE:
			// logic depends on having the 'initialState'
			if (initialState) {
				return { ...initialState };
			}
			return state;

		case SearchContextActionType.UPDATE_SORT_BY:
			updatedState = {
				...state,
				sortBy: { ...state.sortBy, selected: action.payload },
			};
			break;

		case SearchContextActionType.UPDATE_PAGE_SIZE:
			updatedState = {
				...state,
				pageSize: { ...state.pageSize, selected: action.payload },
			};
			break;

		case SearchContextActionType.UPDATE_FACET: {
			const { facetName, facetValue, active } = action.payload;

			const newFacets = state.facets.map((facet) => {
				if (facet.value !== facetName) return facet;

				const newOptions = facet.options.map((option) => {
					if (option.value !== facetValue) return option;
					// Update option
					return { ...option, isSelected: active };
				});

				return { ...facet, options: newOptions };
			});

			updatedState = { ...state, facets: newFacets };
			break;
		}

		case SearchContextActionType.UPDATE_FACETS:
			updatedState = { ...state, facets: action.payload };
			break;

		default:
			return state;
	}

	// Re-calculate links if state changed
	if (updatedState !== state) {
		updatedState.links = updateLinks(updatedState, pathname, currentParams);
	}

	return updatedState;
};

export const useSearchContextReducer = (props: SearchContextProviderProps) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const stateKey = `${pathname}?${searchParams.toString()}`;

	// Store initialState in state to compare for Reset (or use a ref equivalent, but logic was in reducer)
	// The original code used a useState tuple [stateKey, initialState]

	const [initialStateStore, setInitialStateStore] = useState<
		[string, SearchContextState]
	>(() => [
		stateKey,
		createStateFromProps({
			...props,
			pathname,
			currentParams: searchParams,
		}),
	]);

	const initialState = useMemo(() => initialStateStore[1], [initialStateStore]);

	// Wrap reducer to inject dependencies (initialState, pathname)
	const reducerWrapper = (
		state: SearchContextState,
		action: SearchContextAction,
	) =>
		searchContextReducer(state, action, initialState, pathname, searchParams);

	const [state, dispatch] = useReducer(reducerWrapper, initialState);

	useEffect(() => {
		const initialStateKey = initialStateStore[0];

		if (stateKey === initialStateKey) {
			return;
		}

		const updatedInitialState = createStateFromProps({
			...props,
			pathname,
			currentParams: searchParams,
		});

		setInitialStateStore([stateKey, updatedInitialState]);
		// reset state
		dispatch({ type: SearchContextActionType.RESET_STATE });
	}, [stateKey, props, pathname]);

	return [state, dispatch] as [
		SearchContextState,
		Dispatch<SearchContextAction>,
	];
};
